import { LitElement, css, html, when, nothing } from "@lit"
import styles from "@nova/styles"
import { formatPrice, updateCartQuantity, t } from "@nova/utils"
import * as paths from "@nova/paths"

class NovaCartItem extends LitElement {
  static properties = {
    item: {
      type: Object,
    },
    compareAtPrice: {
      type: String,
    },
    inventoryPolicy: {
      type: Boolean,
    },
    max: {
      type: String,
    },
  }

  constructor() {
    super()
    this.item = {}
    this.compareAtPrice = 0
    this.inventoryPolicy = false
    this.max = null
  }

  get formattedFinalPrice() {
    if (!this.item.final_price) return "Free"
    return formatPrice(this.item.final_price)
  }

  get formattedCompareAtPrice() {
    const comparePrice = Number(this.compareAtPrice)
    if (!comparePrice || comparePrice === 0) return nothing
    return formatPrice(comparePrice)
  }

  _renderPrice() {
    const hasCompareAtPrice = () => {
      const comparePrice =
        Number(this.compareAtPrice) || Number(this.item.compare_at_price)

      return comparePrice > 0 && comparePrice > this.item.final_price
    }

    const hasDiscount = () => {
      return (
        this.item.line_level_total_discount &&
        this.item.final_price < this.item.original_price
      )
    }

    if (this.item.final_price === 0) {
      return html`
        <span>
          <span class="visually-hidden">${window.priceStrings?.regularPrice || 'Regular price'}</span>
          ${this.formattedFinalPrice}
        </span>
      `
    }

    if (hasCompareAtPrice()) {
      return html`
        <div class="cart-item__discounted-prices">
          <span class="price-item--sale">
            <span class="visually-hidden">${window.priceStrings?.salePrice || 'Sale price'}</span>
            ${this.formattedFinalPrice}
          </span>
          <span>
            <span class="visually-hidden">${window.priceStrings?.regularPrice || 'Regular price'}</span>
            <s class="cart-item__compare-at-price">
              ${this.formattedCompareAtPrice}
            </s>
          </span>
        </div>
      `
    }

    if (hasDiscount()) {
      return html`
        <div class="cart-item__discounted-prices">
          <span class="price-item--discount">
            <span class="visually-hidden">${window.priceStrings?.discountPrice || 'Discount price'}</span>
            ${this.formattedFinalPrice}
          </span>
          <span>
            <span class="visually-hidden">${window.priceStrings?.regularPrice || 'Regular price'}</span>
            <s class="cart-item__compare-at-price">
              ${formatPrice(this.item.original_price)}
            </s>
          </span>
        </div>
      `
    }

    return html`
      <span>
        <span class="visually-hidden">${window.priceStrings?.regularPrice || 'Regular price'}</span>
        ${this.formattedFinalPrice}
      </span>
    `
  }

  _renderOptions() {
    const hasOptions = Boolean(
      this.item.options_with_values &&
        this.item.options_with_values[0]?.value !== "Default Title"
    )

    if (!hasOptions) {
      return html`${nothing}`
    }

    const renderOptionElements = this.item.options_with_values.map(
      (option) => html`
        <div class="cart-item__options flex">
          <dt class="visually-hidden">${option.name}:</dt>
          <dd>${option.value}</dd>
        </div>
      `
    )

    return html`
      <svg
        aria-hidden="true"
        width="6"
        height="6"
        viewBox="0 0 6 6"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle id="Ellipse 3" cx="3" cy="3" r="3" fill="#272726" />
      </svg>
      <span class="cart-item__option-wrapper"> ${renderOptionElements} </span>
    `
  }

  _renderDiscounts() {
    const hasDiscounts = Boolean(this.item.line_level_discount_allocations && this.item.line_level_discount_allocations.length > 0)

    if (!hasDiscounts) {
      return html`${nothing}`
    }

    const renderDiscountElements =
      this.item.line_level_discount_allocations.map((discount_allocation) => {
        const { amount, discount_application } = discount_allocation
        const { title } = discount_application
        const amount_str = Number.parseFloat(amount / 100).toFixed(2)
        return html`
          <li class="cart-item__discounts-item flex">
            ${paths.discount} ${title} (-$${amount_str})
          </li>
        `
      })

    return html`
      <ul
        class="cart-item__discounts-wrapper caption c-subdued"
        role="list"
        aria-label="${t("customer.order.discount")}"
      >
        ${renderDiscountElements}
      </ul>
    `
  }

  _handleQuantityChange = (event) => {
    this.updateQuantity(event.detail.value)
    this.dispatchEvent(new CustomEvent("nova-change"))
    this.focusUpdatedInput(event.detail)
  }

  async updateQuantity(quantity) {
    this.loading = true
    await updateCartQuantity(
      [
        {
          id: this.item.key,
          quantity,
        },
      ],
      false
    )
    this.loading = false
    document.dispatchEvent(new CustomEvent("nova:changeQuantityFinished"), {
      bubbles: true,
    })
  }

  focusUpdatedInput(detail) {
    if (detail.key === this.item.key) {
      const input = this.shadowRoot
        .querySelector("nova-quantity-input")
        ?.shadowRoot.querySelector("input")
      input.focus()
    }
  }

  render() {
    return html`
      <div class="cart-item__media">
        <a href=${this.item.url}>
          ${this.item.image
            ? html`<img
                class="cart-item__image cover"
                src=${this.item.image}
                alt=${this.item.product_title}
                width="120px"
              />`
            : nothing}
        </a>
      </div>
      <div class="flex cart-item__content">
        <p class="cart-item__vendor">${this.item.vendor}</p>
        <div class="cart-item__title">
          <a href=${this.item.url}>${this.item.product_title}</a>
        </div>
        <div class="cart-item__price-and-option">
          <div class="cart-item__price-wrapper">${this._renderPrice()}</div>
          ${this._renderOptions()} ${this._renderDiscounts()}
        </div>
        <div class="cart-item__quantity-and-remove">
          ${when(
            !this.item.properties?._dk_gift,
            () => html`
              <nova-quantity-input
                initial=${this.item.quantity}
                key=${this.item.key}
                @changeQuantityStarted=${this._handleQuantityChange}
                class="cart-item__quantity-input"
                max="${!this.inventoryPolicy ? this.max : undefined}"
              ></nova-quantity-input>
            `,
            () => nothing
          )}
          <sl-button
            variant="text"
            class="cart-item__remove"
            @click=${() => this.updateQuantity(0)}
          >
            Remove
          </sl-button>
        </div>
      </div>
    `
  }

  static styles = [
    styles.flex,
    css`
      :host {
        box-sizing: border-box;
        width: 100%;
        padding: 0 var(--nova-space-md) var(--nova-space-md);
        display: grid;
        grid-template-columns: 112px 1fr;
        grid-template-areas: "media content";
        column-gap: 20px;
        align-items: center;
      }
      .cart-item__media {
        grid-area: media;
        display: flex;
      }
      .cart-item__media a {
        height: 100%;
        display: flex;
      }
      .cart-item__media a:focus-visible,
      .cart-item__title a:focus-visible {
        outline: 2px solid -webkit-focus-ring-color;
        outline-offset: 2px;
      }
      .cart-item__media img {
        width: 100%;
        height: auto;
        aspect-ratio: 112/150;
        object-fit: cover;
        border-radius: var(--nova-space-xs);
        mix-blend-mode: multiply;
      }
      .cart-item__content {
        flex-direction: column;
        gap: var(--nova-space-sm);
      }
      .cart-item__vendor {
        font: var(--nova-body-caption);
        line-height: normal;
        margin: 0;
      }
      .cart-item__title {
        font-size: 16px;
        font-family: var(--nova-font-sans-bold);
        line-height: 112.5%;
        font-weight: 700;
        text-transform: uppercase;
      }
      .cart-item__title a {
        color: var(--nova-color-text);
        text-decoration: none;
      }
      .cart-item__options dd {
        margin-left: 0;
      }
      .cart-item__options .visually-hidden {
        position: absolute !important;
        overflow: hidden;
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
        clip: rect(0 0 0 0);
        word-wrap: normal !important;
      }
      .cart-item__price-and-option {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 16px;
        line-height: calc(13 / 16 * 100%);
        flex-wrap: wrap;
      }
      .cart-item__price-wrapper .visually-hidden {
        visibility: hidden;
        display: none;
      }
      .cart-item__discounted-prices {
        margin: 0;
        display: flex;
        gap: var(--nova-space-2xs);
      }
      .cart-item__discounted-prices dd {
        margin: 0;
      }
      .cart-item__compare-at-price {
        color: var(--nova-brand-neutral);
      }
      .cart-item__option-wrapper {
        display: flex;
        gap: var(--nova-space-2xs);
      }
      .cart-item__quantity-and-remove {
        grid-area: quantity-and-remove;
        display: flex;
        align-items: center;
        gap: 12px;
      }
      .cart-item__remove::part(base) {
        color: var(--nova-color-text);
        text-decoration: underline;
        text-underline-offset: 3px;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        letter-spacing: 1px;
        width: min-content;
        min-height: unset;
        margin-bottom: 3px;
      }
      .cart-item__remove::part(label) {
        padding: 0;
      }
      .cart-item__quantity-input {
        display: inline-flex;
        justify-content: end;
        width: 100px;
        height: 40px;
      }
      @media screen and (max-width: 350px) {
        .cart-item__quantity-input {
          width: 75px;
        }
      }
      .cart-item__quantity-input::part(qty-decrease-btn) {
        border-top-left-radius: 50px;
        border-bottom-left-radius: 50px;
        height: var(--nova-mini-cart-quantity-selector-height);
      }
      .cart-item__quantity-input::part(qty-increase-btn) {
        border-top-right-radius: 50px;
        border-bottom-right-radius: 50px;
        height: var(--nova-mini-cart-quantity-selector-height);
      }
      .cart-item__quantity-input::part(qty-wrapper) {
        height: var(--nova-forms-input-height);
      }

      @media screen and (min-width: 768px) {
        :host {
          padding: 0 var(--nova-space-sm) var(--nova-space-sm);
        }
        .cart-item__content {
          gap: var(--nova-space-xsm);
        }
        .cart-item__price-and-option {
          font-size: 14px;
          line-height: calc(12 / 14 * 100%);
        }
        .cart-item__remove::part(base) {
          font-size: 14px;
        }
      }

      .cart-item__discounts-wrapper {
        display: block;
        margin: 0;
        padding-inline-start: 0;
        color: var(--nova-color-subdued);
        font: var(--nova-body-caption);
        letter-spacing: var(--nova-caption-letter-spacing);
      }

      .cart-item__discounts-item {
        gap: var(--nova-space-2xs);

        & .icon-discount {
          stroke: var(--nova-color-brand-charcoal);
          stroke-width: 1.5px;
        }
      }

      .price-item--sale,
      .price-item--discount {
        color: var(--nova-on-sale);
      }
    `,
  ]
}

customElements.define("nova-cart-item", NovaCartItem)
